import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { CatalogWarming } from "./Home";
import { useCategories, useProducts } from "@/lib/queries";

const SORTS = [
    { value: "popularity", label: "Most popular" },
    { value: "newest", label: "Newest" },
    { value: "price", label: "Price: low to high" },
    { value: "price-desc", label: "Price: high to low" },
    { value: "name", label: "Name A–Z" },
];

export default function Shop({ dealsMode = false }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const categories = useCategories();

    const category = searchParams.get("category") || "";
    const search = searchParams.get("q") || "";
    const sort = searchParams.get("sort") || (dealsMode ? "price" : "popularity");
    const strain = searchParams.get("strain") || "";
    const minPrice = searchParams.get("min") || "";
    const maxPrice = searchParams.get("max") || "";
    const inStock = searchParams.get("stock") === "1";
    const page = parseInt(searchParams.get("page") || "1", 10);
    const [searchInput, setSearchInput] = useState(search);
    const [moreFilters, setMoreFilters] = useState(false);

    useEffect(() => {
        setSearchInput(search);
    }, [search]);

    useEffect(() => {
        const t = setTimeout(() => {
            if (searchInput !== search) {
                const next = new URLSearchParams(searchParams);
                if (searchInput) next.set("q", searchInput);
                else next.delete("q");
                next.delete("page");
                setSearchParams(next, { replace: true });
            }
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput]); // eslint-disable-line

    const setParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== "page") next.delete("page");
        setSearchParams(next, { replace: false });
    };

    const params = useMemo(
        () => ({
            category: category || undefined,
            search: search || undefined,
            strain: strain || undefined,
            min_price: minPrice || undefined,
            max_price: maxPrice || undefined,
            in_stock: inStock || undefined,
            on_sale: dealsMode || undefined,
            orderby: sort,
            page,
            per_page: 12,
        }),
        [category, search, strain, minPrice, maxPrice, inStock, dealsMode, sort, page]
    );

    const { data, isLoading, isFetching, isError, refetch } = useProducts(params);
    const products = data?.products || [];

    const strainOptions = useMemo(() => {
        const set = new Set();
        products.forEach((p) => p.strain && set.add(p.strain));
        return Array.from(set).sort();
    }, [products]);

    const activeFilters = [category, search, strain, minPrice, maxPrice].filter(Boolean).length + (inStock ? 1 : 0);

    const clearAll = () => {
        setSearchParams(dealsMode ? { on_sale: "1" } : new URLSearchParams(), { replace: false });
        setSearchInput("");
    };

    return (
        <div className="pt-24 md:pt-32 pb-24" data-testid="shop-page">
            <Seo
                title={dealsMode ? "Deals & Steals — District Greenz DMV" : "Shop the Menu — District Greenz DMV"}
                description={dealsMode
                    ? "Every product currently on sale at District Greenz DMV. Open 24 hours in Washington, DC — cash on delivery or pay at pickup."
                    : "Browse the live District Greenz DMV menu: flower, edibles, vapes, cartridges, concentrates, prerolls and more. Filter by strain, brand and price."}
                path={dealsMode ? "/deals" : "/shop"}
            />

            <div className="container-x">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <p className="eyebrow text-gold">{dealsMode ? "Deals & Steals" : "The Live Menu"}</p>
                        <h1 className="mt-3 font-display text-4xl md:text-6xl font-extrabold text-bone">
                            {dealsMode ? "On sale now." : "The Menu."}
                        </h1>
                    </div>
                    {data && (
                        <p className="font-mono text-xs uppercase tracking-widest text-sage" data-testid="shop-result-count">
                            {data.total} {data.total === 1 ? "product" : "products"}
                            {isFetching && " · syncing"}
                        </p>
                    )}
                </div>

                <div className="sticky top-16 z-30 -mx-5 mt-8 border-y border-leaf/12 bg-ink/90 px-5 py-4 backdrop-blur-xl md:top-20 md:-mx-12 md:px-12" data-testid="shop-filter-bar">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative min-w-[180px] flex-1 md:max-w-xs">
                            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-sage" />
                            <input
                                data-testid="shop-search-input"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search the menu..."
                                className="w-full rounded-full border border-leaf/20 bg-moss/60 py-2.5 pl-10 pr-4 text-sm text-bone placeholder:text-sage/70 focus:border-leaf/60 focus:outline-none"
                            />
                        </div>

                        <select
                            data-testid="shop-sort-select"
                            value={sort}
                            onChange={(e) => setParam("sort", e.target.value === "popularity" && !dealsMode ? "" : e.target.value)}
                            className="rounded-full border border-leaf/20 bg-moss/60 px-4 py-2.5 text-sm text-bone focus:border-leaf/60 focus:outline-none"
                        >
                            {SORTS.map((s) => (
                                <option key={s.value} value={s.value} className="bg-ink">
                                    {s.label}
                                </option>
                            ))}
                        </select>

                        <button
                            data-testid="shop-more-filters"
                            onClick={() => setMoreFilters((s) => !s)}
                            className={`rounded-full border px-4 py-2.5 text-xs font-semibold transition-colors ${
                                moreFilters || activeFilters > 0 ? "border-leaf bg-leaf/15 text-leaf-bright" : "border-leaf/20 bg-moss/60 text-sage hover:text-bone"
                            }`}
                        >
                            Filters{activeFilters > 0 ? ` · ${activeFilters}` : ""}
                        </button>

                        {moreFilters && (
                            <div className="flex w-full flex-wrap items-center gap-3" data-testid="shop-more-filters-panel">
                                {strainOptions.length > 0 && (
                                    <select
                                        data-testid="shop-strain-select"
                                        value={strain}
                                        onChange={(e) => setParam("strain", e.target.value)}
                                        className="rounded-full border border-leaf/20 bg-moss/60 px-4 py-2.5 text-sm text-bone focus:border-leaf/60 focus:outline-none"
                                    >
                                        <option value="" className="bg-ink">All strains</option>
                                        {strainOptions.map((s) => (
                                            <option key={s} value={s} className="bg-ink">{s}</option>
                                        ))}
                                    </select>
                                )}

                                <div className="flex items-center gap-2 rounded-full border border-leaf/20 bg-moss/60 px-4 py-2">
                                    <span className="font-mono text-[10px] uppercase tracking-widest text-sage">$</span>
                                    <input
                                        data-testid="shop-min-price"
                                        type="number"
                                        min="0"
                                        placeholder="min"
                                        value={minPrice}
                                        onChange={(e) => setParam("min", e.target.value)}
                                        className="w-14 bg-transparent text-sm text-bone placeholder:text-sage/60 focus:outline-none"
                                    />
                                    <span className="text-sage">–</span>
                                    <input
                                        data-testid="shop-max-price"
                                        type="number"
                                        min="0"
                                        placeholder="max"
                                        value={maxPrice}
                                        onChange={(e) => setParam("max", e.target.value)}
                                        className="w-14 bg-transparent text-sm text-bone placeholder:text-sage/60 focus:outline-none"
                                    />
                                </div>

                                <button
                                    data-testid="shop-instock-toggle"
                                    onClick={() => setParam("stock", inStock ? "" : "1")}
                                    className={`rounded-full border px-4 py-2.5 text-xs font-semibold transition-colors ${
                                        inStock ? "border-leaf bg-leaf/15 text-leaf-bright" : "border-leaf/20 bg-moss/60 text-sage hover:text-bone"
                                    }`}
                                >
                                    In stock
                                </button>
                            </div>
                        )}

                        {activeFilters > 0 && (
                            <button
                                data-testid="shop-clear-filters"
                                onClick={clearAll}
                                className="flex items-center gap-1.5 rounded-full px-3 py-2.5 text-xs font-semibold text-red-400 hover:text-red-300"
                            >
                                <X size={13} /> Clear ({activeFilters})
                            </button>
                        )}
                    </div>

                    {categories.data?.categories?.length > 0 && (
                        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="shop-category-pills">
                            <button
                                data-testid="category-pill-all"
                                onClick={() => setParam("category", "")}
                                className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                                    !category ? "bg-leaf text-ink" : "border border-leaf/20 bg-moss/60 text-sage hover:text-bone"
                                }`}
                            >
                                All
                            </button>
                            {categories.data.categories.map((c) => (
                                <button
                                    key={c.id}
                                    data-testid={`category-pill-${c.slug}`}
                                    onClick={() => setParam("category", c.slug)}
                                    className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                                        category === c.slug ? "bg-leaf text-ink" : "border border-leaf/20 bg-moss/60 text-sage hover:text-bone"
                                    }`}
                                >
                                    {c.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {isError ? (
                    <div className="mt-12">
                        <CatalogWarming onRetry={refetch} />
                    </div>
                ) : isLoading ? (
                    <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="skeleton aspect-[4/5] rounded-2xl" />
                        ))}
                    </div>
                ) : products.length === 0 ? (
                    <div className="mt-16 flex flex-col items-center gap-4 rounded-3xl border border-leaf/15 bg-moss/40 px-8 py-20 text-center" data-testid="shop-empty-state">
                        <p className="font-display text-2xl font-bold text-bone">Nothing matches that.</p>
                        <p className="text-sm text-sage">Try loosening the filters or searching something else.</p>
                        <button
                            onClick={clearAll}
                            data-testid="shop-empty-clear"
                            className="mt-2 rounded-full bg-leaf px-6 py-3 text-sm font-bold text-ink hover:bg-leaf-bright"
                        >
                            Reset filters
                        </button>
                    </div>
                ) : (
                    <>
                        <div className={`mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4 ${isFetching ? "opacity-60 transition-opacity" : "transition-opacity"}`} data-testid="shop-grid">
                            {products.map((p) => (
                                <ProductCard key={p.id} product={p} />
                            ))}
                        </div>

                        {data.total_pages > 1 && (
                            <div className="mt-14 flex items-center justify-center gap-4" data-testid="shop-pagination">
                                <button
                                    data-testid="shop-pagination-prev"
                                    disabled={data.page <= 1}
                                    onClick={() => setParam("page", String(data.page - 1))}
                                    className="flex h-11 w-11 items-center justify-center rounded-full border border-leaf/25 text-bone hover:border-leaf hover:text-leaf-bright disabled:opacity-30"
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft size={17} />
                                </button>
                                <span className="font-mono text-xs uppercase tracking-widest text-sage">
                                    Page {data.page} of {data.total_pages}
                                </span>
                                <button
                                    data-testid="shop-pagination-next"
                                    disabled={data.page >= data.total_pages}
                                    onClick={() => setParam("page", String(data.page + 1))}
                                    className="flex h-11 w-11 items-center justify-center rounded-full border border-leaf/25 text-bone hover:border-leaf hover:text-leaf-bright disabled:opacity-30"
                                    aria-label="Next page"
                                >
                                    <ChevronRight size={17} />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
